import React, { useState } from 'react';
import { Save, Shield, AlertTriangle, RotateCcw } from 'lucide-react';
// import { toast } from 'react-hot-toast';
import { useAppContext } from '../../contexts/AppContext';

// --- Types ---
interface Feature {
    id: string;
    name: string;
    category: 'Core' | 'Reliability Tools' | 'Validation' | 'Advanced AI' | 'Admin & Security';
    description?: string;
}

interface Plan {
    id: 'trial' | 'standard' | 'premium' | 'enterprise';
    name: string;
    price: string;
    description: string;
    color: string;
}

// --- Constants ---
const FEATURES: Feature[] = [
    // Core
    { id: 'dashboard', name: 'Executive Dashboard', category: 'Core' },
    { id: 'projects', name: 'Project Management', category: 'Core' },
    { id: 'action_tracking', name: 'Action Item Tracking', category: 'Core' },

    // Reliability Tools
    { id: 'fmea', name: 'FMEA Portal (DFMEA/PFMEA)', category: 'Reliability Tools' },
    { id: 'p_diagram', name: 'P-Diagram Generator', category: 'Reliability Tools' },
    { id: 'risk_matrix', name: 'Risk Analysis Matrix', category: 'Reliability Tools' },
    { id: 'control_plan', name: 'Control Plans', category: 'Reliability Tools' },

    // Validation
    { id: 'dvpr', name: 'DVP&R (Test Planning)', category: 'Validation' },
    { id: 'lims', name: 'LIMS (Lab Management)', category: 'Validation' },
    { id: 'physics', name: 'Physics Simulation Lab', category: 'Validation' },
    { id: 'traceability', name: 'Traceability Matrix', category: 'Validation' },

    // AI
    { id: 'ai_assistant', name: 'Magnus AI Assistant', category: 'Advanced AI' },
    { id: 'auto_fmea', name: 'Automated FMEA Generation', category: 'Advanced AI' },
    { id: 'doc_analysis', name: 'Requirement Analysis AI', category: 'Advanced AI' },

    // Admin
    { id: 'sso', name: 'SSO & Advanced Security', category: 'Admin & Security' },
    { id: 'api_access', name: 'API Access', category: 'Admin & Security' },
    { id: 'audit_logs', name: 'Detailed Audit Logs', category: 'Admin & Security' },
    { id: 'custom_roles', name: 'Custom RBAC Roles', category: 'Admin & Security' }
];

const PLANS: Plan[] = [
    { id: 'trial', name: 'Trial Access', price: 'Free', description: '14-Day Limited Trial', color: 'slate' },
    { id: 'standard', name: 'Standard', price: '$499/mo', description: 'Small Teams', color: 'blue' },
    { id: 'premium', name: 'Premium', price: '$1,499/mo', description: 'Growing Orgs', color: 'indigo' },
    { id: 'enterprise', name: 'Enterprise', price: 'Custom', description: 'Full Control', color: 'purple' }
];

// Initial State (Mock Database)
const INITIAL_MATRIX: Record<string, Record<string, boolean>> = {
    trial: {
        dashboard: true, projects: true, action_tracking: true,
        fmea: true, p_diagram: true, risk_matrix: true,
        dvpr: false, lims: false, physics: false,
        ai_assistant: true, auto_fmea: false,
        sso: false, api_access: false
    },
    standard: {
        dashboard: true, projects: true, action_tracking: true,
        fmea: true, p_diagram: true, risk_matrix: true, control_plan: true,
        dvpr: true, traceability: true,
        ai_assistant: true, auto_fmea: true,
        sso: false, api_access: false
    },
    premium: {
        dashboard: true, projects: true, action_tracking: true,
        fmea: true, p_diagram: true, risk_matrix: true, control_plan: true,
        dvpr: true, lims: true, physics: true, traceability: true,
        ai_assistant: true, auto_fmea: true, doc_analysis: true,
        sso: true, audit_logs: true
    },
    enterprise: {
        // All true effectively
        dashboard: true, projects: true, action_tracking: true,
        fmea: true, p_diagram: true, risk_matrix: true, control_plan: true,
        dvpr: true, lims: true, physics: true, traceability: true,
        ai_assistant: true, auto_fmea: true, doc_analysis: true,
        sso: true, api_access: true, audit_logs: true, custom_roles: true
    }
};

const SubscriptionManagementPage: React.FC = () => {
    const { subscriptionConfig, updateSubscriptionConfig } = useAppContext();
    const [matrix, setMatrix] = useState(subscriptionConfig?.matrix || INITIAL_MATRIX);
    const [hasChanges, setHasChanges] = useState(false);

    const togglePermission = (planId: string, featureId: string) => {
        setMatrix(prev => ({
            ...prev,
            [planId]: {
                ...prev[planId],
                [featureId]: !prev[planId][featureId]
            }
        }));
        setHasChanges(true);
    };

    const handleSave = () => {
        updateSubscriptionConfig({ matrix });
        alert("Subscription configuration saved globally! Feature gating is now active.");
        setHasChanges(false);
    };

    const handleReset = () => {
        if (window.confirm("Discard all unsaved changes and revert to last saved state?")) {
            setMatrix(subscriptionConfig?.matrix || INITIAL_MATRIX);
            setHasChanges(false);
        }
    };

    // Group features by category
    const categories = Array.from(new Set(FEATURES.map(f => f.category)));

    return (
        <div className="min-h-screen bg-slate-50 p-6 md:p-8 space-y-8 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-slate-200 pb-6">
                <div>
                    <h1 className="text-3xl font-black text-slate-900 flex items-center gap-3">
                        <Shield className="text-indigo-600" size={32} />
                        Subscription Matrix
                    </h1>
                    <p className="text-slate-500 mt-2 font-medium">Configure feature access for each subscription tier globally.</p>
                </div>
                <div className="flex items-center gap-3">
                    {hasChanges && (
                        <button
                            onClick={handleReset}
                            className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium flex items-center gap-2 transition-colors"
                        >
                            <RotateCcw size={18} /> Revert
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={!hasChanges}
                        className={`px-6 py-2 rounded-lg font-bold shadow-sm flex items-center gap-2 transition-all ${hasChanges
                            ? 'bg-indigo-600 text-white hover:bg-indigo-700 hover:shadow-md hover:scale-105'
                            : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                            }`}
                    >
                        <Save size={18} /> Save Changes
                    </button>
                </div>
            </div>

            {/* Matrix Table */}
            <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="p-6 w-1/3 min-w-[300px]">
                                    <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Features</span>
                                </th>
                                {PLANS.map(plan => (
                                    <th key={plan.id} className="p-6 min-w-[150px] text-center border-l border-slate-100">
                                        <div className={`text-lg font-bold text-${plan.color}-600 capitalize`}>{plan.name}</div>
                                        <div className="text-2xl font-black text-slate-900 mt-1">{plan.price}</div>
                                        <div className="text-xs text-slate-500 mt-1 font-medium">{plan.description}</div>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {categories.map(category => (
                                <React.Fragment key={category}>
                                    {/* Category Header */}
                                    <tr className="bg-slate-50/50">
                                        <td colSpan={PLANS.length + 1} className="px-6 py-3">
                                            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">{category}</span>
                                        </td>
                                    </tr>

                                    {/* Features in Category */}
                                    {FEATURES.filter(f => f.category === category).map(feature => (
                                        <tr key={feature.id} className="hover:bg-slate-50/80 transition-colors group">
                                            <td className="px-6 py-4 border-r border-transparent group-hover:border-slate-100">
                                                <div className="font-semibold text-slate-800">{feature.name}</div>
                                                {feature.description && <div className="text-xs text-slate-400 mt-0.5">{feature.description}</div>}
                                            </td>
                                            {PLANS.map(plan => {
                                                const isEnabled = matrix[plan.id]?.[feature.id] || false;
                                                return (
                                                    <td key={`${plan.id}-${feature.id}`} className="px-6 py-4 text-center border-l border-slate-100">
                                                        <label className="relative inline-flex items-center justify-center cursor-pointer p-2 group-hover:bg-indigo-50/30 rounded-lg transition-colors">
                                                            <input
                                                                type="checkbox"
                                                                className="w-5 h-5 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer shadow-sm transition-all"
                                                                checked={isEnabled}
                                                                onChange={() => togglePermission(plan.id, feature.id)}
                                                            />
                                                        </label>
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Warning / Note */}
            <div className="bg-amber-50 border-l-4 border-amber-400 p-4 rounded-r-lg flex gap-4 shadow-sm items-start">
                <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
                <div>
                    <h4 className="font-bold text-amber-900">Configuration Impact Warning</h4>
                    <p className="text-sm text-amber-800 mt-1 leading-relaxed">
                        Modifying subscription entitlements propagates immediately to the licensing server.
                        Existing customers on legacy plans will retain their current feature set until their next renewal cycle unless &quot;Force Sync&quot; is enabled in backend.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SubscriptionManagementPage;
